/**
 * FenData class for parsing, manipulating and generating FEN strings
 * FEN (Forsyth-Edwards Notation) is a standard notation for describing chess positions
 */
export class FenData {
    _piecePlacement;
    _activeColor;
    _castlingRights;
    _enPassantTarget;
    _halfmoveClock;
    _fullmoveNumber;
    _isValid = true;
    _error = "";
    _chess;
    // Default FEN values
    static DEFAULT_PIECE_PLACEMENT = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR";
    static DEFAULT_ACTIVE_COLOR = "w";
    static DEFAULT_CASTLING_RIGHTS = {
        K: true,
        Q: true,
        k: true,
        q: true,
    };
    static DEFAULT_EN_PASSANT = "-";
    static DEFAULT_HALFMOVE_CLOCK = 0;
    static DEFAULT_FULLMOVE_NUMBER = 1;
    // Valid en passant target squares
    static VALID_EN_PASSANT_TARGETS = [
        "a3",
        "b3",
        "c3",
        "d3",
        "e3",
        "f3",
        "g3",
        "h3",
        "a6",
        "b6",
        "c6",
        "d6",
        "e6",
        "f6",
        "g6",
        "h6",
    ];
    /**
     * Creates a new FenData instance from a FEN string
     * @param fen - The FEN string to parse
     * @param chess - Optional Chess.js instance
     */
    constructor(fen = null, chess = null) {
        // Initialize with default values
        this._piecePlacement = FenData.DEFAULT_PIECE_PLACEMENT;
        this._activeColor = FenData.DEFAULT_ACTIVE_COLOR;
        this._castlingRights = { ...FenData.DEFAULT_CASTLING_RIGHTS };
        this._enPassantTarget = FenData.DEFAULT_EN_PASSANT;
        this._halfmoveClock = FenData.DEFAULT_HALFMOVE_CLOCK;
        this._fullmoveNumber = FenData.DEFAULT_FULLMOVE_NUMBER;
        this._chess = chess;
        if (fen) {
            this.parseFen(fen);
        }
    }
    /**
     * Parses a FEN string into its component parts
     * @param fen - The FEN string to parse
     */
    parseFen(fen) {
        try {
            const parts = fen.split(" ");
            // Store error if not enough parts, but continue parsing what we have
            if (parts.length < 6) {
                this._isValid = false;
                this._error = "FEN string has fewer than 6 parts";
            }
            // Parse piece placement (part 0)
            if (parts.length > 0 && parts[0]) {
                this._piecePlacement = parts[0];
            }
            // Parse active color (part 1)
            if (parts.length > 1 && parts[1]) {
                if (parts[1] === "w" || parts[1] === "b") {
                    this._activeColor = parts[1];
                }
                else {
                    this._isValid = false;
                    this._error += ' Active color must be "w" or "b".';
                    // Still use the default value
                }
            }
            // Parse castling rights (part 2)
            if (parts.length > 2 && parts[2]) {
                const castling = parts[2];
                this._castlingRights = {
                    K: castling.includes("K"),
                    Q: castling.includes("Q"),
                    k: castling.includes("k"),
                    q: castling.includes("q"),
                };
            }
            // Parse en passant target (part 3)
            if (parts.length > 3 && parts[3]) {
                this._enPassantTarget = parts[3];
            }
            // Parse halfmove clock (part 4)
            if (parts.length > 4 && parts[4]) {
                const halfmove = parseInt(parts[4], 10);
                if (!isNaN(halfmove) && halfmove >= 0) {
                    this._halfmoveClock = halfmove;
                }
                else {
                    this._isValid = false;
                    this._error += " Halfmove clock must be a non-negative integer.";
                    // Keep the default value
                }
            }
            // Parse fullmove number (part 5)
            if (parts.length > 5 && parts[5]) {
                const fullmove = parseInt(parts[5], 10);
                if (!isNaN(fullmove) && fullmove >= 1) {
                    this._fullmoveNumber = fullmove;
                }
                else {
                    this._isValid = false;
                    this._error += " Fullmove number must be a positive integer.";
                    // Keep the default value
                }
            }
            // Validate what we've parsed
            this.validate();
        }
        catch (e) {
            this._isValid = false;
            this._error = e instanceof Error ? e.message : "Invalid FEN format";
        }
    }
    /**
     * Validates the piece placement section of the FEN
     * @returns True if valid, false otherwise
     */
    validatePiecePlacement() {
        try {
            const ranks = this._piecePlacement.split("/");
            // Check if we have exactly 8 ranks
            if (ranks.length !== 8) {
                this._error = "FEN must have 8 ranks";
                return false;
            }
            // Check each rank
            for (const rank of ranks) {
                let squares = 0;
                for (let i = 0; i < rank.length; i++) {
                    const char = rank[i];
                    if (/[1-8]/.test(char)) {
                        squares += parseInt(char, 10);
                    }
                    else if (/[pnbrqkPNBRQK]/.test(char)) {
                        squares += 1;
                    }
                    else {
                        this._error = `Invalid piece character: ${char}`;
                        return false;
                    }
                }
                if (squares !== 8) {
                    this._error = `Rank must have 8 squares, found ${squares}`;
                    return false;
                }
            }
            // Check kings - allow empty board (no kings) or exactly one of each
            const whiteKingCount = (this._piecePlacement.match(/K/g) || []).length;
            const blackKingCount = (this._piecePlacement.match(/k/g) || []).length;
            const isEmptyBoard = whiteKingCount === 0 && blackKingCount === 0;
            const hasValidKings = whiteKingCount === 1 && blackKingCount === 1;
            if (!isEmptyBoard && !hasValidKings) {
                this._error = `Invalid king count - White kings: ${whiteKingCount}, Black kings: ${blackKingCount}`;
                return false;
            }
            return true;
        }
        catch (e) {
            this._error = e instanceof Error ? e.message : "Invalid piece placement";
            return false;
        }
    }
    /**
     * Validates the en passant target square
     * @returns True if valid, false otherwise
     */
    validateEnPassantTarget() {
        if (this._enPassantTarget === "-") {
            return true;
        }
        // En passant target must be a valid square in the 3rd or 6th rank
        if (!FenData.VALID_EN_PASSANT_TARGETS.includes(this._enPassantTarget)) {
            this._error = `Invalid en passant target: ${this._enPassantTarget}`;
            return false;
        }
        return true;
    }
    /**
     * Validates the FEN using Chess.js
     * @returns True if valid, false otherwise
     */
    validateWithChessJs() {
        if (!this._chess) {
            return true;
        }
        try {
            // Use provided Chess instance or create a new one
            const chess = this._chess;
            const validateResult = chess.validate_fen(this.fen);
            if (!validateResult.valid) {
                this._error =
                    validateResult.error || "Invalid FEN according to Chess.js";
                return false;
            }
            return true;
        }
        catch (e) {
            this._error =
                e instanceof Error ? e.message : "Chess.js validation error";
            return false;
        }
    }
    /**
     * Validates the entire FEN data
     * @returns True if valid, false otherwise
     */
    validate() {
        // Reset validation state
        this._isValid = true;
        // First validate with our internal checks
        if (!this.validatePiecePlacement()) {
            this._isValid = false;
            return false;
        }
        if (!this.validateEnPassantTarget()) {
            this._isValid = false;
            return false;
        }
        // Then validate with chess.js
        if (!this.validateWithChessJs()) {
            this._isValid = false;
            return false;
        }
        return this._isValid;
    }
    /**
     * Gets the current FEN string
     * @returns The FEN string representation of the current position
     */
    get fen() {
        const castlingStr = this.getCastlingString();
        return [
            this._piecePlacement,
            this._activeColor,
            castlingStr,
            this._enPassantTarget,
            this._halfmoveClock,
            this._fullmoveNumber,
        ].join(" ");
    }
    /**
     * Gets a normalized version of the FEN string
     * Attempts to fix common issues with invalid FENs
     */
    get normalizedFen() {
        // If already valid, just return the current FEN
        if (this.isValid) {
            return this.fen;
        }
        try {
            // Check if piece placement can be normalized
            const ranks = this._piecePlacement.split("/");
            // If we don't have 8 ranks, return default position
            if (ranks.length !== 8) {
                return null;
            }
            // Try to normalize each rank
            let normalizedPiecePlacement = "";
            let whiteKingCount = 0;
            let blackKingCount = 0;
            for (let i = 0; i < 8; i++) {
                const rank = ranks[i];
                let squares = 0;
                let normalizedRank = "";
                for (let j = 0; j < rank.length; j++) {
                    const char = rank[j];
                    if (/[1-8]/.test(char)) {
                        squares += parseInt(char, 10);
                    }
                    else if (/[pnbrqkPNBRQK]/.test(char)) {
                        squares += 1;
                        normalizedRank += char;
                        // Count kings
                        if (char === "K")
                            whiteKingCount++;
                        if (char === "k")
                            blackKingCount++;
                    }
                    // Skip invalid characters
                }
                // Pad with empty squares if needed
                if (squares < 8) {
                    normalizedRank += 8 - squares;
                }
                else if (squares > 8) {
                    // Too many squares, can't fix
                    return null;
                }
                normalizedPiecePlacement += (i > 0 ? "/" : "") + normalizedRank;
            }
            // Verify king count - allow empty board (no kings) or exactly one of each
            const isEmptyBoard = whiteKingCount === 0 && blackKingCount === 0;
            const hasValidKings = whiteKingCount === 1 && blackKingCount === 1;
            if (!isEmptyBoard && !hasValidKings) {
                return null;
            }
            // Use the normalized piece placement with other properties
            return [
                normalizedPiecePlacement,
                this._activeColor,
                this.getCastlingString(),
                this._enPassantTarget,
                this._halfmoveClock,
                this._fullmoveNumber,
            ].join(" ");
        }
        catch (e) {
            console.error("Error normalizing FEN:", e);
            return null;
        }
    }
    /**
     * Gets the castling rights as a string
     * @returns The castling rights string (e.g., "KQkq", "Kq", "-")
     */
    getCastlingString() {
        let result = "";
        if (this._castlingRights.K)
            result += "K";
        if (this._castlingRights.Q)
            result += "Q";
        if (this._castlingRights.k)
            result += "k";
        if (this._castlingRights.q)
            result += "q";
        return result || "-";
    }
    /**
     * Resets the FEN data to the default starting position
     */
    reset() {
        this._piecePlacement = FenData.DEFAULT_PIECE_PLACEMENT;
        this._activeColor = FenData.DEFAULT_ACTIVE_COLOR;
        this._castlingRights = { ...FenData.DEFAULT_CASTLING_RIGHTS };
        this._enPassantTarget = FenData.DEFAULT_EN_PASSANT;
        this._halfmoveClock = FenData.DEFAULT_HALFMOVE_CLOCK;
        this._fullmoveNumber = FenData.DEFAULT_FULLMOVE_NUMBER;
        this._isValid = true;
        this._error = "";
    }
    // Getters and setters for FEN properties
    /**
     * Gets the piece placement
     */
    get piecePlacement() {
        return this._piecePlacement;
    }
    /**
     * Sets the piece placement
     */
    set piecePlacement(value) {
        this._piecePlacement = value;
        this.validate();
    }
    /**
     * Gets the active color
     */
    get activeColor() {
        return this._activeColor;
    }
    /**
     * Sets the active color
     */
    set activeColor(value) {
        this._activeColor = value;
    }
    /**
     * Gets the castling rights
     */
    get castlingRights() {
        return { ...this._castlingRights };
    }
    /**
     * Sets the castling rights
     */
    set castlingRights(value) {
        this._castlingRights = { ...value };
    }
    /**
     * Updates individual castling rights
     * @param right - The castling right to update (K, Q, k, or q)
     * @param value - The new value
     */
    updateCastlingRight(right, value) {
        this._castlingRights[right] = value;
    }
    /**
     * Gets the en passant target
     */
    get enPassantTarget() {
        return this._enPassantTarget;
    }
    /**
     * Sets the en passant target
     */
    set enPassantTarget(value) {
        this._enPassantTarget = value;
        // When setting a new en passant target, we need to validate it
        // and update the isValid property accordingly
        const isEnPassantValid = this.validateEnPassantTarget();
        if (!isEnPassantValid) {
            this._isValid = false;
        }
        else {
            // Re-validate the entire FEN to ensure it's still valid
            this.validate();
        }
    }
    /**
     * Gets the halfmove clock
     */
    get halfmoveClock() {
        return this._halfmoveClock;
    }
    /**
     * Sets the halfmove clock
     */
    set halfmoveClock(value) {
        if (value < 0) {
            throw new Error("Halfmove clock must be a non-negative integer");
        }
        this._halfmoveClock = value;
    }
    /**
     * Gets the fullmove number
     */
    get fullmoveNumber() {
        return this._fullmoveNumber;
    }
    /**
     * Sets the fullmove number
     */
    set fullmoveNumber(value) {
        if (value < 1) {
            throw new Error("Fullmove number must be a positive integer");
        }
        this._fullmoveNumber = value;
    }
    /**
     * Gets whether the FEN is valid
     */
    get isValid() {
        return this._isValid;
    }
    /**
     * Gets the error message if the FEN is invalid
     */
    get error() {
        return this._error;
    }
    /**
     * Checks if the white king is on its original e1 square
     * @returns True if the white king is on e1
     */
    isWhiteKingOnE1() {
        const ranks = this._piecePlacement.split("/");
        const whiteRow = ranks[7];
        const expandedRow = whiteRow.replace(/\d/g, (num) => ".".repeat(Number(num)));
        return expandedRow[4] === "K";
    }
    /**
     * Checks if the black king is on its original e8 square
     * @returns True if the black king is on e8
     */
    isBlackKingOnE8() {
        const ranks = this._piecePlacement.split("/");
        const blackRow = ranks[0];
        const expandedRow = blackRow.replace(/\d/g, (num) => ".".repeat(Number(num)));
        return expandedRow[4] === "k";
    }
}
